import { Form, Input, Select, Checkbox, Typography } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  AttributeEditor,
  Wizard,
  Select as CloudScapeSelect,
  SpaceBetween,
} from "@cloudscape-design/components";
import IncredableContent from "../components/incredable-content";
import { useGetBasicAllProvidersQuery } from "../redux/api/provider/provider";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import RHFTextField from "../components/RHF/RHFTextField";
import RHFSelect from "../components/RHF/RHFSelect";
import { useGetAllCategoryQuery } from "../redux/api/category/category";
import RHFTextArea from "../components/RHF/RHFTextArea";
import RHFDatePicker from "../components/RHF/RHFDatePicker";
import { useGetAllMembersQuery } from "../redux/api/manageadmin/manageadmin";
import { ComPdfSignatureRequestStepTwo } from "../requests/signature-requests/add-eSignature/compdf-signature-request";
import { useAddDigitalformCompdfSignatureRequestMutation } from "../redux/api/signaturerequest/signaturerequest";
import { useParams, useNavigate } from "react-router-dom";
import {
  RecipientType,
  SignatureRequestType,
} from "../redux/api/signaturerequest/types";
import dayjs from "dayjs";
import { SpinnerContext } from "../context/spinner/spinner";
import { ToastContext } from "../context/toast/incredable-toast";

const { Paragraph } = Typography;

const SignatureWizard = () => {
  const { digitalformId } = useParams();
  const navigate = useNavigate();
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);

  const methods = useForm<SignatureRequestType>({
    defaultValues: {
      id: "",
      primaryRecipient: { type: "provider" },
      addlRecipients: [],
      subject: "",
      message: "",
      categoryId: "",
      alertDays: "-1",
      reviewRequired: false,
    },
  });

  const { handleSubmit, clearErrors } = methods;

  const [hasExpiration, setHasExpiration] = useState(false);
  const [hasRecipients, setHasRecipients] = useState(false);
  const { data: providers = [] } = useGetBasicAllProvidersQuery();
  const { data: categories } = useGetAllCategoryQuery();
  const { data: members } = useGetAllMembersQuery();
  const spinnerContext = useContext(SpinnerContext);
  const toastContext = useContext(ToastContext);
  const [addlRecipients, setAddlRecipients] = useState<
    { key: "member" | "email"; value: string }[]
  >([{ key: "member", value: "" }]);
  const [addDigitalformCompdfSignatureRequest] =
    useAddDigitalformCompdfSignatureRequestMutation();

  const onSubmit = async (data: SignatureRequestType, annoteJson?: string) => {
    const selectedProvider = providers.find(
      (provider) => provider.id + "" === data.primaryRecipient.id + ""
    );

    console.log({ annoteJson });

    const primaryRecipient = {
      id: "",
      action: "sign",
      type: "provider" as const,
      provider: selectedProvider ? selectedProvider : undefined,
      member: undefined,
      emailAddress: undefined,
      annotationJSON: annoteJson,
    };
    const additionalRecipients: RecipientType[] = addlRecipients
      .map((item) => {
        if (item.key === "member") {
          const member = selectedMember(item?.value);
          return {
            id: "",
            action: "sign",
            type: "member" as const,
            member: member,
            provider: undefined,
            emailAddress: undefined,
            xfdfString: undefined,
          };
        } else if (item.key === "email") {
          return {
            id: "",
            action: "sign",
            type: "email" as const,
            emailAddress: item.value,
            member: undefined,
            provider: undefined,
          };
        }
        return { id: "", action: "sign", type: item.key };
      })
      ?.filter((item) => !!item?.member || !!item?.emailAddress);

    const signatureRequest = {
      ...data,
      primaryRecipient: primaryRecipient,
      addlRecipients: additionalRecipients,
      expirationDate: data.expirationDate
        ? moment(data.expirationDate).format("YYYY-MM-DD")
        : undefined,
      alertDays: data.alertDays ?? "-1",
    };

    spinnerContext?.showSpinner();
    try {
      if (!!digitalformId) {
        const { data } = await addDigitalformCompdfSignatureRequest({
          formId: digitalformId,
          ...signatureRequest,
        });
        navigate(`/requests/${data?.id}`);
        toastContext?.openInfoNotification({
          message: `Signature request sent successfully`,
          placement: "topRight",
        });
      }
    } catch (error: any) {
      toastContext?.openErrorNotification({
        message: error?.data?.errorMessage || "Failed to add signature",
        placement: "topRight",
      });
    }
    spinnerContext?.hidespinner();
  };

  const handleWizardSubmit = (annoteJson: string) => {
    handleSubmit((data) => onSubmit(data, annoteJson))();
  };

  const handleCancel = () => {
    methods.reset();
    navigate(-1);
  };

  const selectedMember = useCallback(
    (memberId: string) => {
      return members?.find((member) => member.id + "" === memberId + "");
    },
    [members]
  );

  const provider = methods?.watch("primaryRecipient.id");
  useEffect(() => {
    clearErrors("primaryRecipient.id");
  }, [provider, clearErrors]);
  const subject = methods?.watch("subject");
  useEffect(() => {
    clearErrors("subject");
  }, [subject, clearErrors]);
  const categoryId = methods?.watch("categoryId");
  useEffect(() => {
    clearErrors("categoryId");
  }, [categoryId, clearErrors]);

  return (
    <IncredableContent>
      <FormProvider {...methods}>
        <Form layout="vertical">
          {activeStepIndex !== 4 && (
            <Wizard
              i18nStrings={{
                stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
                collapsedStepsLabel: (stepNumber, stepsCount) =>
                  `Step ${stepNumber} of ${stepsCount}`,
                skipToButtonLabel: (step, stepNumber) =>
                  `Skip to ${step.title}`,
                navigationAriaLabel: "Steps",
                cancelButton: "Cancel",
                previousButton: "Previous",
                nextButton: "Next",
                submitButton: "Review",
              }}
              allowSkipTo
              onNavigate={({ detail }) => {
                const provider = methods?.watch("primaryRecipient.id");
                const subject = methods?.watch("subject");
                const category = methods?.watch("categoryId");
                if (activeStepIndex === 0 && !provider) {
                  methods.setError("primaryRecipient.id", {
                    type: "manual",
                    message: "This field is required",
                  });
                  return;
                }
                if (activeStepIndex === 2 && !subject) {
                  methods.setError("subject", {
                    type: "manual",
                    message: "This field is required",
                  });
                  return;
                }
                if (activeStepIndex === 3 && !category) {
                  methods.setError("categoryId", {
                    type: "manual",
                    message: "This field is required",
                  });
                  return;
                }
                setActiveStepIndex(detail.requestedStepIndex);
              }}
              onCancel={handleCancel}
              activeStepIndex={activeStepIndex}
              steps={[
                {
                  title: "Recipients",
                  content: (
                    <SpaceBetween size="l">
                      <RHFSelect
                        formItemProps={{ label: "Primary Recipient" }}
                        controllerProps={{ name: "primaryRecipient.id" }}
                        selectProps={{
                          onClear: () => {
                            methods?.resetField("primaryRecipient.id");
                          },
                          placeholder: "Select Primary Recipient",
                          allowClear: true,
                          showSearch: true,
                          style: { width: "100%" },
                          options: (providers ?? [])
                            .slice()
                            .sort((a, b) =>
                              (a?.lastName ?? "").localeCompare(
                                b?.lastName ?? ""
                              )
                            )
                            .map((provider) => ({
                              label: `${provider.lastName} ${provider.firstName}`,
                              value: provider.id,
                            })),
                        }}
                        rules={{ required: "This field is required" }}
                      />
                      <Checkbox onChange={(e) => setHasRecipients(true)}>
                        Add additional recipients who need to sign this form?
                      </Checkbox>
                      {hasRecipients && (
                        <AttributeEditor
                          onAddButtonClick={() =>
                            setAddlRecipients([
                              ...addlRecipients,
                              { key: "member", value: "" },
                            ])
                          }
                          onRemoveButtonClick={({ detail: { itemIndex } }) => {
                            setAddlRecipients(
                              addlRecipients.filter((_, i) => i !== itemIndex)
                            );
                          }}
                          items={addlRecipients}
                          addButtonText="Add new recipient"
                          definition={[
                            {
                              label: "Recipient Type",
                              control: (item, index) => (
                                <CloudScapeSelect
                                  selectedOption={{
                                    label:
                                      item.key === "member"
                                        ? "Member"
                                        : item.key === "email"
                                        ? "Email address"
                                        : "",
                                    value: item.key,
                                  }}
                                  onChange={({ detail }) => {
                                    const newItems = [...addlRecipients];
                                    newItems[index].key = detail.selectedOption
                                      ?.value as "member" | "email";
                                    newItems[index].value = "";
                                    setAddlRecipients(newItems);
                                  }}
                                  options={[
                                    { label: "Member", value: "member" },
                                    { label: "Email address", value: "email" },
                                  ]}
                                />
                              ),
                            },
                            {
                              label: "Value",
                              control: (item, index) =>
                                item.key === "member" ? (
                                  <Select
                                    value={
                                      addlRecipients[index]?.value || undefined
                                    }
                                    onChange={(value) => {
                                      const newItems = [...addlRecipients];
                                      newItems[index].value = value ?? "";
                                      setAddlRecipients(newItems);
                                    }}
                                    allowClear
                                    showSearch
                                    style={{ width: "100%" }}
                                    placeholder="Search for member"
                                    options={members?.map((member) => ({
                                      label: `${member?.firstName ?? ""} ${
                                        member?.lastName ?? ""
                                      }`,
                                      value: member?.id,
                                    }))}
                                    filterOption={(input, option) =>
                                      (option?.label as string)
                                        ?.toLowerCase()
                                        .includes(input.toLowerCase())
                                    }
                                  />
                                ) : (
                                  <Input
                                    value={item.value}
                                    onChange={(e) => {
                                      const newItems = [...addlRecipients];
                                      newItems[index].value = e.target.value;
                                      setAddlRecipients(newItems);
                                    }}
                                    placeholder="Enter email address"
                                  />
                                ),
                            },
                          ]}
                          empty="No additional recipients added."
                        />
                      )}
                    </SpaceBetween>
                  ),
                },
                {
                  title: "Expiration Settings",
                  content: (
                    <SpaceBetween size="l">
                      <Paragraph type="secondary">
                        Set an expiration date for the signature request. You
                        can also choose to receive a reminder before it expires.
                      </Paragraph>
                      <div>
                        <Form.Item>
                          <Checkbox
                            checked={hasExpiration}
                            onChange={(e) => setHasExpiration(e.target.checked)}
                          >
                            Set an expiration date for this signature request?
                          </Checkbox>
                        </Form.Item>
                        {hasExpiration && (
                          <div>
                            <RHFDatePicker
                              formItemProps={{
                                label: "Expiration Date",
                              }}
                              controllerProps={{
                                name: `expirationDate`,
                              }}
                              datePickerProps={{
                                disabledDate: (current) => {
                                  return (
                                    current && current < dayjs().endOf("day")
                                  );
                                },
                              }}
                            />
                            <RHFSelect
                              formItemProps={{
                                label: "Reminder Alert",
                              }}
                              controllerProps={{
                                name: "alertDays",
                                rules: { required: false },
                              }}
                              selectProps={{
                                allowClear: true,
                                showSearch: true,
                                placeholder: "Select Reminder Alert",
                                options: [1, 3, 5, 7].map((d) => ({
                                  label: `${d} day${
                                    d > 1 ? "s" : ""
                                  } before expiration`,
                                  value: d.toString(),
                                })),
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </SpaceBetween>
                  ),
                  isOptional: true,
                },
                {
                  title: "Title & Message",
                  content: (
                    <SpaceBetween size="l">
                      <RHFTextField
                        formItemProps={{
                          label: "Subject / Title",
                        }}
                        inputProps={{
                          placeholder: "Enter Subject / Title",
                        }}
                        controllerProps={{
                          name: `subject`,
                        }}
                        rules={{ required: "This field is required" }}
                      />

                      <RHFTextArea
                        formItemProps={{ label: "Message Body" }}
                        controllerProps={{ name: "message" }}
                      />
                    </SpaceBetween>
                  ),
                },
                {
                  title: "Save to Safe",
                  content: (
                    <SpaceBetween size="l">
                      <RHFSelect
                        formItemProps={{ label: "Category" }}
                        controllerProps={{
                          name: "categoryId",
                        }}
                        selectProps={{
                          allowClear: true,
                          showSearch: true,
                          onClear: () => {
                            methods?.resetField("categoryId");
                          },
                          style: { width: "100%" },
                          placeholder: "Search to category",
                          options: categories?.map((category) => {
                            return {
                              label: category?.name,
                              searchValue: category?.name,
                              value: category?.id,
                            };
                          }),
                        }}
                        rules={{ required: "This field is required" }}
                      />
                      <Checkbox
                        name="reviewRequired"
                        checked={methods?.watch("reviewRequired")}
                        onChange={(value) =>
                          methods?.setValue(
                            "reviewRequired",
                            !methods?.watch("reviewRequired")
                          )
                        }
                      >
                        Require review before saving to safe
                      </Checkbox>
                    </SpaceBetween>
                  ),
                },
                {
                  title: "Review",
                  content: <></>,
                },
              ]}
            />
          )}
          {activeStepIndex === 4 && (
            <ComPdfSignatureRequestStepTwo
              providerId={methods.getValues("primaryRecipient.id")}
              onClickBack={() => {
                setActiveStepIndex(3);
              }}
              handleWizardSubmit={handleWizardSubmit}
            />
          )}
        </Form>
      </FormProvider>
    </IncredableContent>
  );
};

export default SignatureWizard;
